import React from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { required } from '../../../utils/validations';
import { InputWrapper } from '../../common/input-wrapper';
import { AlertService } from '../../../services/alert.service';
import { useModalClose } from '../../../utils/useModalClose';
import useSWRMutation from 'swr/mutation';
import { RouterInput, trpc } from '../../../api/trpc';
import { Item } from './OtcRecommendationTab';
import { MediaInput, MediaInputValue } from '../../common/media-input';
import { uploadFile } from '../../../services/file-service';
import { OtcommerceTagsSelector } from '../../common/otcommerce-tags-selector';

type Form = {
  disabled: boolean;
  objectId: string;
  image: MediaInputValue[] | undefined;
  video: MediaInputValue[] | undefined;
  name: string;
  otcommerceTags: { value: string }[];
  price: number | undefined;
  approve: number | undefined;
  ctr: number | undefined;
  boost: number | undefined;
  stock: string;
};

export type ModalProps = {
  close: () => void;
  reject: () => void;
  item?: Item;
  hasEnoughBoost?: boolean;
};

export const AddOtcRecommendation: React.FC<ModalProps> = ({
  close,
  reject,
  item,
  hasEnoughBoost,
}) => {
  const {
    formState: { isSubmitting },
    handleSubmit,
    control,
    setValue,
  } = useForm<Form>({
    defaultValues: item
      ? {
          objectId: item.objectId,
          image: item.image
            ? [
                {
                  type: 'exist' as const,
                  file: item.image,
                },
              ]
            : undefined,
          video: item.video
            ? [
                {
                  type: 'exist' as const,
                  file: item.video,
                },
              ]
            : undefined,
          name: item.name || '',
          otcommerceTags: item?.otcommerceTags?.length
            ? item.otcommerceTags.map((tag) => ({
                value: tag,
              }))
            : [],
          price: item.price || undefined,
          stock: item.stock || '',
          approve: item.approve || undefined,
          ctr: Number((item.ctr * 100).toFixed(6)) || undefined,
          boost: item.boost || undefined,
        }
      : {},
  });

  const { trigger: triggerUpdate } = useSWRMutation(
    ['otcRecommendationAdmin.update'],
    (_k, { arg }: { arg: RouterInput['otcRecommendationAdmin']['update'] }) =>
      trpc.otcRecommendationAdmin.update.mutate(arg),
  );
  const { trigger: triggerCreate } = useSWRMutation(
    ['otcRecommendationAdmin.create'],
    (_k, { arg }: { arg: RouterInput['otcRecommendationAdmin']['create'] }) =>
      trpc.otcRecommendationAdmin.create.mutate(arg),
  );

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'otcommerceTags',
  });

  const onSubmit = handleSubmit(async (form) => {
    let imageId: string | null;
    if (form.image?.length) {
      if (form.image[0].type === 'new') {
        const uploadedFile = await uploadFile(form.image[0].file, true);
        imageId = uploadedFile.file.id;
      } else {
        imageId = form.image[0].file.id;
      }
    } else {
      imageId = null;
    }

    let videoId: string | null;
    if (form.video?.length) {
      if (form.video[0].type === 'new') {
        const uploadedFile = await uploadFile(form.video[0].file, true);
        videoId = uploadedFile.file.id;
      } else {
        videoId = form.video[0].file.id;
      }
    } else {
      videoId = null;
    }

    const data = {
      objectId: form.objectId,
      imageId,
      videoId,
      name: form.name ? form.name : null,
      otcommerceTags: form.otcommerceTags
        ? form.otcommerceTags.map((tag) => tag.value)
        : [],
      price: form.price ? Number(form.price) : null,
      approve: form.approve ? Number(form.approve) : null,
      boost: form.boost ? Number(form.boost) : null,
      stock: form.stock ? form.stock : null,
      ctrResetAt: form.ctr ? null : new Date(),
    };
    if (item) {
      await triggerUpdate({
        id: item.id,
        data: data,
      });
    } else {
      await triggerCreate(data);
    }
    AlertService.success();
    close();
  });

  const modalRef = useModalClose(reject);

  return (
    <div className="modal-dialog" ref={modalRef}>
      <form className="modal-content" onSubmit={onSubmit}>
        <div className="modal-header border-bottom-0 p-4">
          <h3>{item ? 'Редактировать' : 'Добавить'} рекомендацию</h3>
          <button
            type="button"
            className="btn-close"
            onClick={() => reject()}
            disabled={isSubmitting}
          />
        </div>
        <div className="modal-body p-4">
          <Controller
            control={control}
            name="objectId"
            rules={{
              ...required,
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                title="Артикул"
                error={fieldState.error?.message}
                required
              >
                <input className="form-control mb-3" type="text" {...field} />
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name="image"
            render={({ field, fieldState }) => (
              <InputWrapper
                theme="light"
                className="mt-3"
                title="Фото товара (JPEG, PNG, TIFF, JPG, WebP)"
                error={fieldState.error?.message}
              >
                <MediaInput
                  {...field}
                  className="form-control mb-3"
                  fileTypes={[
                    'image/png',
                    'image/jpeg',
                    'image/tiff',
                    'image/webp',
                  ]}
                />
              </InputWrapper>
            )}
          />

          <Controller
            control={control}
            name="video"
            rules={{
              validate: (value) => {
                if (!value?.length) return true;
                return (
                  value[0].file.size < 2000000 ||
                  'Файл должен быть  весом не более 2 MB'
                );
              },
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                theme="light"
                className="mt-3"
                title="Видео товара (MP4, MOV, AVI, WMV, MKV и WebM, GIF)"
                error={fieldState.error?.message}
              >
                <MediaInput
                  {...field}
                  className="form-control mb-3"
                  fileTypes={['video/*', 'image/gif']}
                />
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name="name"
            render={({ field, fieldState }) => (
              <InputWrapper
                title="Название товара"
                error={fieldState.error?.message}
              >
                <input className="form-control mb-3" type="text" {...field} />
              </InputWrapper>
            )}
          />
          <div className={'py-2 border-bottom'}>
            <div className={'d-flex gap-2 '}>
              <div>Теги</div>
            </div>

            <div className={'d-flex flex-wrap d-flex gap-2'}>
              {fields.map((field, index) => {
                return (
                  <div className={'d-flex gap-2 pb-2'} key={field.id}>
                    <Controller
                      control={control}
                      name={`otcommerceTags.${index}`}
                      render={({ field }) => {
                        return (
                          <div
                            className={
                              'border-gray-200 border p-2 rounded d-flex hover-bg-light cursor-pointer'
                            }
                            onClick={() => {
                              remove(index);
                            }}
                          >
                            {field.value.value}
                            <div className=" justify-content-center">
                              <i className="bi bi-x" />
                            </div>
                          </div>
                        );
                      }}
                    />
                  </div>
                );
              })}
            </div>

            <OtcommerceTagsSelector
              onAppend={(value) => {
                append({ value: value });
              }}
            />
          </div>

          <Controller
            control={control}
            name="price"
            render={({ field, fieldState }) => (
              <InputWrapper
                title="Сумма выплаты"
                error={fieldState.error?.message}
              >
                <input className="form-control mb-3" type="number" {...field} />
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name="approve"
            render={({ field, fieldState }) => (
              <InputWrapper title="%Выкупа" error={fieldState.error?.message}>
                <input className="form-control mb-3" type="number" {...field} />
              </InputWrapper>
            )}
          />
          {item ? (
            <>
              <Controller
                control={control}
                name="ctr"
                render={({ field, fieldState }) => (
                  <InputWrapper title="CTR%" error={fieldState.error?.message}>
                    <div className="d-flex gap-2">
                      <input
                        className="form-control mb-3"
                        readOnly
                        {...field}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-success mb-3 justify-content-end"
                        disabled={isSubmitting}
                        onClick={() => {
                          setValue('boost', 500);
                          field.onChange('');
                        }}
                      >
                        Сбросить
                      </button>
                    </div>
                  </InputWrapper>
                )}
              />
              {hasEnoughBoost || (!hasEnoughBoost && item.boost) ? (
                <Controller
                  control={control}
                  name="boost"
                  render={({ field, fieldState }) => (
                    <InputWrapper
                      title="Разгон"
                      error={fieldState.error?.message}
                    >
                      <input
                        className="form-control mb-3"
                        type="number"
                        {...field}
                      />
                    </InputWrapper>
                  )}
                />
              ) : null}
            </>
          ) : null}

          <Controller
            control={control}
            name="stock"
            render={({ field, fieldState }) => (
              <InputWrapper title="Склад" error={fieldState.error?.message}>
                <input className="form-control" type="text" {...field} />
              </InputWrapper>
            )}
          />
        </div>
        <div className="modal-footer justify-content-end">
          <button
            type="submit"
            className="btn btn-success"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : item ? (
              'Сохранить'
            ) : (
              'Добавить'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
